<template>
    <div class="-m-5 md:m-0 h-full flex-1 overflow-hidden">
        <tabulator
            :columns="columns"
            :actions="actions"
            ajax-path="wideband/tickets"
            :ajax-params="ajaxParams"            
            :refresh.sync="refreshTable"
            :loading="tableIsLoading"
            :loading-text="tableLoadingText"
            @rowSelectionChanged="preview"
        />
        <app-modal 
            width="lg:w-2/3"
            bg="bg-gray-200"
            :show.sync="showPreviewModal"
            @close="showPreviewModal = false"
        >
            <template slot="header">Trouble Ticket Details</template>
            <template slot="body">
                <div class="card">
                    <table class="table">
                        <tr>
                            <td width="200"><strong>Wideband Ticket ID</strong></td>
                            <td>
                                <div>{{ ticket.id }}</div>
                            </td>
                        </tr>
                        <tr>
                            <td><strong>Created At</strong></td>
                            <td>
                                <div>{{ ticket.created_at }}</div>
                            </td>
                        </tr>
                        <tr>
                            <td><strong>Updated At</strong></td>
                            <td>
                                <div>{{ ticket.updated_at }}</div>
                            </td>
                        </tr>
                        <tr>
                            <td><strong>Severity</strong></td>
                            <td>
                                <div>{{ ticket.severity }}</div>
                            </td>
                        </tr>
                        <tr>
                            <td><strong>Status</strong></td>
                            <td>
                                <div class="flex items-center justify-between">
                                    <div>
                                        {{ ticket.status }}<br>
                                        <span class="text-xs text-gray-600">{{ ticket.last_notification_type }}</span>
                                    </div>
                                    <app-button 
                                        class="btn-blue ml-5"
                                        v-if="ticket.last_notification_type == 'AppointmentRequired'"
                                        @click="showAppointmentAvailabilityModal = true"
                                    >Find Appointment</app-button>
                                </div>
                            </td>                            
                        </tr>
                        <tr v-if="ticket.last_notification_type == 'IncidentResolved'">
                            <td><strong>Your Response</strong></td>
                            <td>
                                <table class="table">
                                    <tr>
                                        <td>
                                            <radio
                                                v-model="clearanceForm.clearanceType"
                                                alt-value="ClearanceAccepted"
                                            >Yes, the Trouble Ticket is resolved</radio>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>
                                            <radio
                                                v-model="clearanceForm.clearanceType"
                                                alt-value="ClearanceRejected"
                                            >No, the Trouble Ticket is not resolved</radio>
                                            <div
                                                v-if="clearanceForm.clearanceType === 'ClearanceRejected'"
                                            >
                                                <app-select
                                                    v-model="clearanceForm.rejectionCode"
                                                    :options="[
                                                        {
                                                            id: 'ACRJ1401',
                                                            label: 'Service not restored'
                                                        },
                                                        {
                                                            id: 'ACRJ1402',
                                                            label: 'Fault Reoccurred'
                                                        },
                                                        {
                                                            id: 'ACRJ1403',
                                                            label: 'More information required'
                                                        }
                                                    ]"
                                                    value-key="id"
                                                    label-key="label"
                                                    placeholder="Reason"
                                                />
                                                <app-textarea
                                                    v-model="clearanceForm.description"
                                                    placeholder="Note"
                                                />
                                            </div>
                                        </td>
                                    </tr>                                    
                                    <tr>
                                        <td>
                                            <app-button 
                                                class="btn-blue"
                                                :loading="clearanceFormIsLoading"
                                                @click="submitClearanceForm"
                                            >Submit</app-button>
                                        </td>
                                    </tr>
                                </table>
                            </td>                            
                        </tr>
                        <tr>
                            <td><strong>Description</strong></td>
                            <td>
                                <div class="whitespace-pre-wrap">{{ ticket.description }}</div>
                            </td>                            
                        </tr>                                            
                    </table>
                </div>
                <h2 class="font-thin tracking-widest uppercase mt-6 mb-3">Updates</h2>
                <tabulator
                    :data="ticket.updates"
                    :columns="[
                        {
                            title: 'Created',
                            field: 'created_at_for_human',
                            minWidth: 190,
                        },
                        {
                            title: 'Status',
                            field: 'update_data',
                            minWidth: 150,
                            formatter({_cell}) {
                                return `<div>${_cell.value.status}<br><span class='text-xs text-gray-600'>${_cell.value.notificationType}</span></div>`;
                            }
                        },
                        {
                            title: 'Comment',
                            field: 'update_data',
                            minWidth: 300,
                            formatter({_cell}) {
                                if (_cell.value.comment) {
                                    return `<div class='whitespace-pre-wrap'>${_cell.value.comment}</div>`;
                                }
                            }
                        }
                    ]"
                    :show-header="false"
                    :show-footer="false"
                />
            </template>
        </app-modal>
        <app-modal 
            width="md:w-1/2 lg:w-1/3"
            :show.sync="showAppointmentAvailabilityModal"
            @close="() => {
                availableAppointmentForm.reset();
            }"
        >
            <template slot="header">Check Appointment Availability</template>
            <template slot="body">
                <app-select 
                    v-model="availableAppointmentForm.demandType"
                    placeholder="Demand Type"
                    :options="['Standard Install', 'Non EU Jumper Only', 'Service Restoration']"
                    :error="availableAppointmentForm.errors.get('demandType')"
                    @updated="availableAppointmentForm.errors.clear('demandType')"
                    required
                />
                <app-select 
                    v-model="availableAppointmentForm.slotType"
                    placeholder="Slot Type"
                    :options="['AM', 'PM']"
                    :error="availableAppointmentForm.errors.get('slotType')"
                    @updated="availableAppointmentForm.errors.clear('slotType')"
                    required
                />                
                <div class="flex items-center justify-end mt-5">
                    <app-button 
                        class="btn-white"
                        @click="showAppointmentAvailabilityModal = false"
                    >Cancel</app-button>
                    <app-button 
                        class="btn-blue ml-2"
                        :loading="checkingAppointmentAvailability"
                        @click="checkAvailableAppointment"
                    >Check</app-button>
                </div>
            </template>
        </app-modal>
        <app-modal 
            width="md:w-1/2 lg:w-1/3"
            :show.sync="showAvailableAppointmentsModal"
        >
            <template slot="header">Available Appointment Schedules</template>
            <template slot="body">
                <div
                    v-for="(a, key) in appointments"
                    class="px-3 py-2 flex items-center justify-between mb-2 rounded border"
                >
                    <div>{{ a.appointmentFormatted }}</div>
                    <app-button
                        class="btn-blue"
                        @click="selectAppointment(a)"
                    >Select</app-button>
                </div>
            </template>
        </app-modal>
        <app-modal 
            width="md:w-1/2 lg:w-1/3"
            :show.sync="showReserveAppointmentModal"
            @close="() => {
                createOrderForm.reset();
                createOrderForm.errors.clear();
            }"
        >
            <template slot="header">Reserve Appointment</template>
            <template slot="body">
                <app-input
                    v-model="appointment.appointmentFormatted"
                    placeholder="Date"
                    disable
                />
                <app-input
                    v-model="reserveAppointmentForm.contactName"
                    placeholder="Contact Name"
                    :error="reserveAppointmentForm.errors.get('contactName')"
                    @updated="reserveAppointmentForm.errors.clear('contactName')"
                    required
                    focus
                />
                <app-input
                    v-model="reserveAppointmentForm.contactPhone"
                    placeholder="Contact Phone"
                    :error="reserveAppointmentForm.errors.get('contactPhone')"
                    @updated="reserveAppointmentForm.errors.clear('contactPhone')"
                    required
                />
                <div class="flex items-center justify-end mt-5">
                    <app-button 
                        class="btn-white"
                        @click="showReserveAppointmentModal = false"
                    >Cancel</app-button>
                    <app-button 
                        class="btn-blue ml-2"
                        :loading="reserveAppointmentFormIsLoading"
                        @click="reserveAppointment"
                    >Reserve Appointment</app-button>
                </div>
            </template>
        </app-modal>
    </div>
</template>

<script>
    import { mapGetters } from 'vuex';
    import Form from '@/helpers/Form';

    export default {
        name: 'widebandOutstandingTickets',
        data() {
            return {
                showPreviewModal: false,
                showAppointmentAvailabilityModal: false,
                showAvailableAppointmentsModal: false,
                showReserveAppointmentModal: false,
                tableIsLoading: false,
                tableLoadingText: null,
                checkingAppointmentAvailability: false,
                reserveAppointmentFormIsLoading: false,
                clearanceFormIsLoading: false,
                ajaxParams: {type: 'outstanding'},
                refreshTable: false,
                q: null,
                availableAppointmentForm: new Form({
                    nbnLocationId: null,
                    demandType: null,
                    slotType: null,
                }),
                reserveAppointmentForm: new Form({
                    nbnLocationId: null,
                    contactName: null,
                    contactPhone: null,
                    timeSlot: null,
                    startTimeRaw: null,
                    endTimeRaw: null,
                    demandType: null,
                }),
                clearanceForm: new Form({
                    clearanceType: 'ClearanceAccepted',
                    description: 'We accept your resolution',
                    employee: 'Access Seeker',
                    rejectionCode: null,
                }),
                columns: [
                    {
                        title: 'ID',
                        field: 'id',
                        sorter: 'number',
                        minWidth: 100,
                    },
                    {
                        title: 'Product ID',
                        field: 'product_id',
                        sorter: 'number',
                        minWidth: 200,
                    },
                    {
                        title: 'Created',
                        field: 'created_at_for_human',
                        sorter: 'string',
                        minWidth: 190,
                    },
                    {
                        title: 'Last Updated',
                        field: 'updated_at_for_human',
                        sorter: 'string',
                        minWidth: 190,
                    },
                    {
                        title: 'Category',
                        field: 'category',
                        sorter: 'string',
                        minWidth: 200,
                    },
                    {
                        title: 'Status',
                        field: 'status',
                        sorter: 'string',
                        minWidth: 150,
                    },
                    {
                        title: 'Last Notification',
                        field: 'last_notification_type',
                        sorter: 'string',
                        minWidth: 200,
                    },                    
                ],
                actions: [
                    {
                        label: 'Sync',
                        icon: () => import('@/icons/Sync.vue'),
                        handler: this.sync
                    }
                ],
            }
        },
        computed: {
            ...mapGetters({
                tickets: 'wideband/tickets/items',
                ticket: 'wideband/tickets/selected',
                appointments: 'wideband/appointments/items',
                appointment: 'wideband/appointments/selected',
            })
        },
        methods: {
            preview(tickets) {
                if (tickets.length) {
                    this.availableAppointmentForm.set({
                        nbnLocationId: tickets[0].product.nbn_location_id,
                    });
                    this.reserveAppointmentForm.set({
                        nbnLocationId: tickets[0].product.nbn_location_id,
                    });
                    this.showPreviewModal = true;
                    this.$store.commit('wideband/tickets/setSelected', tickets[0]);
                }
            },
            submitClearanceForm() {
                this.clearanceFormIsLoading = true;
                this.$store.dispatch('wideband/tickets/clearance', this.clearanceForm.payload())
                    .then(() => {
                        this.clearanceFormIsLoading = false;
                        this.refreshTable = true;
                        this.$swal.fire({
                            type: 'success',
                            title: 'Success!',
                            text: 'Clearance response was successfully submitted.'
                        });
                    })
                    .catch(({ response }) => {
                        this.clearanceFormIsLoading = false;
                        this.clearanceForm.errors.record(response.data)
                    });
            },
            sync() {
                this.tableIsLoading = true;
                this.tableLoadingText = 'Syncing...';
                this.$store.dispatch('wideband/tickets/sync', 'outstanding')
                    .then(() => {
                        this.tableIsLoading = false;
                        this.refreshTable = true;
                        this.$toast.fire({
                            type: 'success',
                            title: 'Syncing complete.'
                        });
                    })
                    .catch(() => {
                        this.tableIsLoading = false;
                    })
            },
            checkAvailableAppointment() {
                this.checkingAppointmentAvailability = true;
                this.$store.dispatch('wideband/appointments/check', this.availableAppointmentForm.payload())
                    .then(() => {
                        this.checkingAppointmentAvailability = false;
                        this.showAvailableAppointmentsModal = true;
                    })
                    .catch(({ response }) => {
                        this.checkingAppointmentAvailability = false;
                        this.availableAppointmentForm.errors.record(response.data);
                    })
            },
            selectAppointment(appointment) {
                this.$store.commit('wideband/appointments/setSelected', appointment);
                this.reserveAppointmentForm.set({
                    timeSlot: appointment.timeSlot,
                    startTimeRaw: appointment.startTimeRaw,
                    endTimeRaw: appointment.endTimeRaw,
                    demandType: this.availableAppointmentForm.demandType,
                });

                this.showReserveAppointmentModal = true;
            },
            reserveAppointment() {
                this.reserveAppointmentFormIsLoading = true;
                this.$store.dispatch('wideband/appointments/reserve', this.reserveAppointmentForm.payload())
                    .then(() => {
                        this.reserveAppointmentFormIsLoading = false;
                        this.showReserveAppointmentModal = false;
                        this.showAvailableAppointmentsModal = false;
                        this.showAppointmentAvailabilityModal = false;
                        this.$swal.fire({
                            type: 'success',
                            title: 'Success',
                            text: 'Appointment was successfully reserved.'
                        })
                    })
                    .catch(({ response }) => {
                        this.reserveAppointmentFormIsLoading = false;
                        this.reserveAppointmentForm.errors.record(response.data);
                    });
            }
        }
    }
</script>